import {
  DEFAULT_PRIMARY_CTA,
  SECONDARY_CTA_VIEW_PRICING,
} from './shared-ctas';
import type { PageCopy } from './page-copy-types';

// ─── /integrations (index) ────────────────────────────────────────────────
// dynamic content lives in src/lib/integrations.ts (integrations + INTEGRATION_CATEGORIES)
export const INTEGRATIONS_INDEX_COPY: PageCopy = {
  eyebrow: 'Integrations · Plug BizRnR into your stack',
  h1: 'BizRnR Integrations',
  lede:
    'BizRnR currently publishes three verified customer-facing connections: Google Calendar for supported appointment workflows, HubSpot for contact synchronization, and Salesforce for opportunity synchronization. Each page states the deployed scope and required validation.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  ctaSecondary: SECONDARY_CTA_VIEW_PRICING,
  sections: [
    {
      id: 'logos',
      eyebrow: 'Verified connections',
      heading: 'Review the integrations deployed today',
      body: 'Open an integration for the supported scope, setup workflow, validation checklist, limitations, and provider link.',
    },
    {
      id: 'categories',
      eyebrow: 'Browse by category',
      heading: 'Choose the system your workflow needs',
      body: 'Google Calendar supports configured appointment workflows. HubSpot and Salesforce support their documented synchronization scopes; they do not imply that every call creates every possible CRM object.',
    },
    {
      id: 'final',
      eyebrow: 'Need a different integration?',
      heading: 'Need a different integration?',
      body: 'If your required system is not listed, contact BizRnR before subscribing. A roadmap request or custom workflow is not a deployed native integration until it is documented and tested.',
    },
  ],
  metadata: {
    title: 'BizRnR Integrations: AI Voice Agent + Your Tools',
    description:
      'Review verified BizRnR connections for Google Calendar, HubSpot, and Salesforce, including deployed scope, setup steps, limitations, and validation guidance.',
    canonical: 'https://bizrnr.com/integrations',
  },
};

// ─── /integrations/[partner] (dynamic shell) ──────────────────────────────
// dynamic content lives in src/lib/integrations.ts (integration.name,
// integration.tagline, integration.intro, integration.useCases, integration.steps,
// integration.faqs, integration.partnerUrl)
export const INTEGRATIONS_PARTNER_COPY: PageCopy = {
  eyebrow: '{INTEGRATION_CATEGORIES[integration.category].label} Integration',
  h1: 'BizRnR + {integration.name}',
  lede: '{integration.tagline}',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  // canonical-divergence: partner-page secondary CTA is partner-specific.
  ctaSecondary: { label: 'Visit {integration.name} →', href: '{integration.partnerUrl}' },
  sections: [
    { heading: 'How it works' },
    { heading: 'Three ways small businesses use this' },
    { heading: 'How to connect' },
    { heading: 'FAQs' },
    { heading: 'Connect BizRnR + {integration.name}', body: 'Authorize the connection, test a representative workflow, and verify the result in both systems before relying on it operationally.' },
  ],
  metadata: {
    title: 'BizRnR + {integration.name}: AI Voice Agent Integration',
    description: '{integration.tagline} Review the verified scope, setup steps, limitations, validation checklist, and current provider documentation.',
    canonical: 'https://bizrnr.com/integrations/{slug}',
  },
};

// ─── /business (directory index) ──────────────────────────────────────────
export const BUSINESS_INDEX_COPY: PageCopy = {
  h1: 'AI-Powered Business Directory',
  lede:
    'Discover opted-in businesses using BizRnR. Listings describe the contact details, services, location, and AI-assisted reception workflow supplied by each business owner; availability and response behavior depend on that business’s configuration.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  sections: [
    { heading: 'Browse by Industry' },
    { heading: 'Get Your Business Listed', body: 'Complete a useful public profile, configure the applicable reception workflow, and opt in to the directory. Incomplete provisioning records remain outside search indexing.' },
  ],
  faqs: [
    { q: 'What is the BizRnR Business Directory?', a: 'The directory contains opted-in public profiles for businesses using BizRnR. Each owner supplies the business details and controls the applicable AI-assisted reception workflow.' },
    { q: 'How do I get my business listed?', a: 'Activate the applicable BizRnR entitlement, complete the required business profile fields, and keep directory visibility enabled. Incomplete profiles are not submitted for indexing.' },
    { q: 'Can I contact businesses through the directory?', a: 'A complete listing can provide the business contact methods and configured AI-assisted experience. Actual availability and routing depend on the individual business.' },
  ],
  metadata: {
    title: 'Business Directory | BizRnR',
    description:
      'Browse opted-in BizRnR business profiles with owner-supplied services, locations, contact details, and configured AI-assisted reception information.',
    canonical: 'https://bizrnr.com/business',
  },
};

// ─── /business/[slug] (tenant mini-site shell) ────────────────────────────
// dynamic content lives in the tenants Supabase row (settings.business_description,
// settings.industry, settings.city, settings.state, settings.twilio_phone_number,
// settings.agent_id, settings.company_logo)
export const BUSINESS_SLUG_COPY: PageCopy = {
  h1: '{businessName}',
  lede: '',
  // canonical-divergence: tenant mini-site CTA dials the tenant directly, not the BizRnR signup.
  ctaPrimary: { label: 'Call Now', href: 'tel:{phone}' },
  sections: [
    { heading: 'Talk to {businessName} — powered by AI, available 24/7', body: 'Every call answered on the first ring. Every question answered, day or night.' },
    { heading: 'Chat with us', body: 'Tap the chat icon in the corner' },
    { heading: 'Powered by BizRnR — Always On. Always Answering.' },
  ],
  metadata: {
    title: '{businessName} — AI-Powered by BizRnR',
    description: '{businessName} — AI-Powered by BizRnR',
    canonical: 'https://bizrnr.com/business/{slug}',
  },
};
